import React, { Suspense } from 'react'

import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom'

import { Loading } from '@elgatoylacaja/components'
import { compose } from 'lodash/fp'
import { AppContext, AppContextData } from './contexts/AppContext'

const LazyDemoRoutes = React.lazy(() =>
  import(/* webpackChunkName: "DemoRoutes" */ './DemoRoutes')
)

const Terms = React.lazy(() =>
  import(/* webpackChunkName: "Terms" */ './views/Terms')
)

const Thanks = React.lazy(() =>
  import(/* webpackChunkName: "Thanks" */ './views/Thanks')
)

const Intro = React.lazy(() =>
  import(/* webpackChunkName: "Intro" */ './views/Intro')
)
const Empezar = React.lazy(() =>
  import(/* webpackChunkName: "Empezar" */ './views/Empezar')
)
const Definiciones = React.lazy(() =>
  import(/* webpackChunkName: "Definiciones" */ './views/Definiciones')
)
const Rasgos = React.lazy(() =>
  import(/* webpackChunkName: "Rasgos" */ './views/Rasgos')
)
const Regressors = React.lazy(() =>
  import(/* webpackChunkName: "Regressors" */ './views/Regressors')
)
const Respuestas = React.lazy(() =>
  import(/* webpackChunkName: "Respuestas" */ './views/Respuestas')
)

type WrappedComponentType =
  | React.ComponentType<RouteComponentProps<any>>
  | React.ComponentType<any>

const condition = (appState: AppContextData) => (
  f: (x: AppContextData) => boolean
) => (r: string) => (WrappedComponent: WrappedComponentType) => (
  p: RouteComponentProps
) => {
  return p.match.isExact ? (
    f(appState) ? (
      <WrappedComponent {...p} />
    ) : (
      <Redirect to={r} {...p} />
    )
  ) : null
}

type LinkedRouteConfig = { prev?: string; route: string; next?: string }

class LinkedRoutes {
  paths: Array<[string, React.ComponentType<any>]>

  constructor(paths: Array<[string, React.ComponentType<any>]>) {
    this.paths = paths
  }

  config = () =>
    this.paths.map(([path, component], index, list): [
      LinkedRouteConfig,
      React.ComponentType
    ] => [
      {
        prev: index === 0 ? undefined : list[index - 1][0],
        route: path,
        next: index === list.length - 1 ? undefined : list[index + 1][0],
      },
      component,
    ])
}

const ExperimentRoutes = new LinkedRoutes([
  ['/terms', Terms],
  ['/intro', Intro],
  ['/definiciones', Definiciones],
  ['/empezar', Empezar],
  ['/rasgos', Rasgos],
  ['/regresores', Regressors],
  ['/thanks', Thanks],
]).config()

const LazySwitchRoutes = () => {
  const { appState } = React.useContext(AppContext)

  const stateCondition = condition(appState)

  const alwaysTrue = stateCondition(_ => true)('')

  const visited = (r: string) =>
    stateCondition(state => state.visited.includes(r))(r)

  const notVisited = (r: string) =>
    stateCondition(state => !state.visited.includes(r))

  const requirePrevious = ({ prev, route, next }: LinkedRouteConfig) =>
    compose(
      prev !== undefined ? visited(prev) : alwaysTrue,
      next !== undefined ? notVisited(route)(next) : alwaysTrue
    )

  return (
    <Switch>
      <Redirect from="/" exact to="/terms" />
      <Suspense fallback={<Loading loading fixed />}>
        {ExperimentRoutes.map(([config, component]) => (
          <Route
            key={config.route}
            path={config.route}
            exact
            render={requirePrevious(config)(component)}
          />
        ))}
        <Route
          path="/respuestas"
          exact
          render={requirePrevious({
            prev: '/thanks',
            route: '/respuestas',
          })(Respuestas)}
        />
        {process.env.REACT_APP_ENVIRONMENT !== 'production' ? (
          <LazyDemoRoutes appState={appState} />
        ) : null}
      </Suspense>
    </Switch>
  )
}

export default LazySwitchRoutes
